/**
 * Plotea sign-in (`VITE_APP_BRAND=plotea`): Microsoft, Google (only when the
 * identity service has it configured), email + password and magic link.
 * Sign-up is open; the API reports `signup_disabled` when the kill switch is
 * on. Contract: docs/architecture/plotea.md ("Authentication", "OAuth flow").
 */
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AuthShell, NoticeBanner } from '../components/Auth'
import { BrandHeader } from '../components/Logo'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { login, register, resendVerification } from '../lib/endpoints/auth'
import {
  getPloteaProviders,
  ploteaApiError,
  ploteaLoginError,
  ploteaReturnTo,
  requestMagicLink,
  requestPasswordReset,
  startPloteaOAuth,
  verifyMagicLink,
  type PloteaSocialProvider,
} from '../lib/endpoints/ploteaAuth'
import { api } from '../lib/http'
import { ApiError } from '../lib/errors'
import { qk } from '../lib/queryKeys'

type Mode = 'login' | 'register' | 'magic' | 'forgot'

/** Provider-list retries before the page offers a manual retry. */
export const PROVIDER_RETRIES = 3

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError)
    return ploteaApiError(error.serverCode) ?? error.message
  return error instanceof Error ? error.message : fallback
}

function MicrosoftMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 21 21" className="size-4">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}

function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" className="size-4">
      <path
        fill="#EA4335"
        d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.5 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.8 6C12.4 13.7 17.7 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17.1z"
      />
      <path
        fill="#FBBC05"
        d="M10.5 28.7c-.5-1.4-.8-3-.8-4.7s.3-3.2.8-4.7l-7.8-6C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.8-6z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.5 0 11.9-2.1 15.8-5.8l-7.4-5.7c-2.1 1.4-4.8 2.3-8.4 2.3-6.3 0-11.6-4.2-13.5-9.9l-7.8 6C6.6 42.6 14.6 48 24 48z"
      />
    </svg>
  )
}

export function PloteaLoginPage() {
  const search = typeof window === 'undefined' ? '' : window.location.search
  const params = new URLSearchParams(search)
  const returnTo = ploteaReturnTo(search)
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(
    ploteaLoginError(params.get('error')),
  )
  const [notice, setNotice] = useState<string | null>(
    params.get('verified') === 'true'
      ? 'Adres email potwierdzony. Możesz się zalogować.'
      : params.get('reset') === 'true'
        ? 'Hasło zmienione. Zaloguj się nowym hasłem.'
        : null,
  )
  const [unverified, setUnverified] = useState(false)
  const [loading, setLoading] = useState(false)
  const nav = useNavigate()
  const qc = useQueryClient()

  // The API and identity service scale to zero on staging: the first call
  // after a cold start can fail or report `available: false`. Retry with
  // backoff, keep polling while unavailable, and never cache a failure as
  // "Google/magic link not configured".
  const providers = useQuery({
    queryKey: ['plotea', 'auth-providers'],
    queryFn: getPloteaProviders,
    staleTime: (query) => (query.state.data?.available === false ? 0 : 60_000),
    retry: PROVIDER_RETRIES,
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 4_000),
    refetchInterval: (query) =>
      query.state.data?.available === false ? 5_000 : false,
  })
  const google = providers.data?.social.includes('google') ?? false
  const magicLink = providers.data?.magic_link ?? false
  const signupEnabled = providers.data?.signup_enabled ?? true

  const reset = () => {
    setError(null)
    setNotice(null)
    setUnverified(false)
  }

  const run = async (action: () => Promise<void>, fallback: string) => {
    reset()
    setLoading(true)
    try {
      await action()
    } catch (e) {
      if (e instanceof ApiError && e.code === 'EmailNotVerified') {
        setUnverified(true)
        setError(
          'Najpierw potwierdź adres email: kliknij link z wiadomości, którą wysłaliśmy.',
        )
      } else {
        setError(errorMessage(e, fallback))
      }
    } finally {
      setLoading(false)
    }
  }

  const onOAuth = (provider: PloteaSocialProvider) =>
    run(async () => {
      const { redirect_url } = await startPloteaOAuth(provider, returnTo)
      window.location.assign(redirect_url)
    }, 'Nie udało się rozpocząć logowania')

  const onLogin = () =>
    run(async () => {
      await login(email, password)
      await qc.invalidateQueries({ queryKey: qk.me() })
      if (returnTo === '/site') await nav({ to: '/site', replace: true })
      else window.location.assign(returnTo)
    }, 'Błąd logowania')

  const onRegister = () =>
    run(async () => {
      await register(email, password, name)
      setMode('login')
      setNotice(
        'Konto utworzone. Sprawdź skrzynkę i kliknij link, aby potwierdzić adres email.',
      )
    }, 'Błąd rejestracji')

  const onMagicLink = () =>
    run(async () => {
      await requestMagicLink(email)
      setNotice(
        'Jeśli adres jest poprawny, wysłaliśmy na niego link do logowania.',
      )
    }, 'Nie udało się wysłać linku')

  const onForgot = () =>
    run(async () => {
      await requestPasswordReset(email)
      setNotice(
        'Jeśli konto z tym adresem istnieje, wysłaliśmy na nie link do zmiany hasła.',
      )
    }, 'Nie udało się wysłać wiadomości')

  const onResend = () =>
    run(async () => {
      await resendVerification(email)
      setNotice('Wysłaliśmy nowy link weryfikacyjny.')
    }, 'Nie udało się wysłać wiadomości')

  const heading =
    mode === 'register'
      ? 'Utwórz konto Plotea'
      : mode === 'magic'
        ? 'Zaloguj się linkiem email'
        : mode === 'forgot'
          ? 'Nie pamiętasz hasła?'
          : 'Zaloguj się do Plotea'

  return (
    <AuthShell>
      <div className="mb-6" data-testid="login-heading">
        <BrandHeader topic="Analiza działek" compact metadata={null} />
      </div>
      <h1 className="mb-6 text-xl font-semibold text-foreground">{heading}</h1>

      {notice && <NoticeBanner message={notice} tone="success" centered />}
      {error && <NoticeBanner message={error} tone="error" />}
      {providers.isError && (
        <div className="mb-4" data-testid="providers-error">
          <NoticeBanner
            message="Nie udało się pobrać wszystkich metod logowania."
            tone="error"
          />
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            data-testid="providers-retry"
            onClick={() => void providers.refetch()}
          >
            Spróbuj ponownie
          </Button>
        </div>
      )}
      {providers.data && !providers.data.available && (
        <NoticeBanner
          message="Logowanie jest chwilowo niedostępne. Spróbuj ponownie za chwilę."
          tone="error"
        />
      )}

      <div className="space-y-2">
        <Button
          variant="outline"
          className="w-full"
          size="lg"
          data-testid="oauth-microsoft"
          disabled={loading}
          onClick={() => void onOAuth('microsoft')}
        >
          <MicrosoftMark />
          Kontynuuj z Microsoft
        </Button>
        {google && (
          <Button
            variant="outline"
            className="w-full"
            size="lg"
            data-testid="oauth-google"
            disabled={loading}
            onClick={() => void onOAuth('google')}
          >
            <GoogleMark />
            Kontynuuj z Google
          </Button>
        )}
      </div>

      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-card px-2 text-muted-foreground">lub email</span>
        </div>
      </div>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          void (mode === 'register'
            ? onRegister()
            : mode === 'magic'
              ? onMagicLink()
              : mode === 'forgot'
                ? onForgot()
                : onLogin())
        }}
      >
        {mode === 'register' && (
          <div className="space-y-1.5">
            <Label htmlFor="register-name">Imię i nazwisko</Label>
            <Input
              id="register-name"
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="login-email">Email</Label>
          <Input
            id="login-email"
            name="email"
            type="email"
            autoComplete="username"
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        {mode !== 'magic' && mode !== 'forgot' && (
          <div className="space-y-1.5">
            <Label htmlFor="login-password">Hasło</Label>
            <Input
              id="login-password"
              name="password"
              type="password"
              autoComplete={
                mode === 'register' ? 'new-password' : 'current-password'
              }
              placeholder={mode === 'register' ? 'Min. 8 znaków' : undefined}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}
        <Button
          className="w-full"
          size="lg"
          type="submit"
          data-testid={mode === 'register' ? 'register-submit' : 'login-submit'}
          disabled={loading}
        >
          {mode === 'register'
            ? 'Utwórz konto'
            : mode === 'magic'
              ? 'Wyślij link'
              : mode === 'forgot'
                ? 'Wyślij link do zmiany hasła'
                : 'Zaloguj się'}
        </Button>
      </form>

      {unverified && (
        <Button
          variant="link"
          className="mt-2 h-auto p-0"
          data-testid="resend-verification"
          disabled={loading || email === ''}
          onClick={() => void onResend()}
        >
          Wyślij link weryfikacyjny ponownie
        </Button>
      )}

      <div className="mt-5 space-y-1 text-center text-sm text-muted-foreground">
        {mode !== 'magic' && magicLink && (
          <p>
            <Button
              variant="link"
              className="h-auto p-0"
              data-testid="mode-magic"
              onClick={() => {
                reset()
                setMode('magic')
              }}
            >
              Zaloguj się linkiem email (bez hasła)
            </Button>
          </p>
        )}
        {mode === 'login' && (
          <p>
            <Button
              variant="link"
              className="h-auto p-0"
              data-testid="mode-forgot"
              onClick={() => {
                reset()
                setMode('forgot')
              }}
            >
              Nie pamiętasz hasła?
            </Button>
          </p>
        )}
        {mode === 'login' && signupEnabled && (
          <p>
            Nie masz konta?{' '}
            <Button
              variant="link"
              className="h-auto p-0"
              data-testid="mode-register"
              onClick={() => {
                reset()
                setMode('register')
              }}
            >
              Zarejestruj się
            </Button>
          </p>
        )}
        {mode !== 'login' && (
          <p>
            <Button
              variant="link"
              className="h-auto p-0"
              data-testid="mode-login"
              onClick={() => {
                reset()
                setMode('login')
              }}
            >
              Wróć do logowania hasłem
            </Button>
          </p>
        )}
      </div>
    </AuthShell>
  )
}

/** `/magic-link?token=`: completes a magic-link sign-in. */
export function PloteaMagicLinkPage() {
  const token =
    typeof window === 'undefined'
      ? ''
      : (new URLSearchParams(window.location.search).get('token') ?? '')
  const nav = useNavigate()
  const qc = useQueryClient()
  const result = useQuery({
    queryKey: ['plotea', 'magic-link', token],
    enabled: token !== '',
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      await verifyMagicLink(token)
      await qc.invalidateQueries({ queryKey: qk.me() })
      await nav({ to: '/site', replace: true })
      return true
    },
  })
  const failed = token === '' || result.isError
  return (
    <AuthShell>
      <div className="mb-6">
        <BrandHeader topic="Analiza działek" compact metadata={null} />
      </div>
      {failed ? (
        <>
          <NoticeBanner
            message={
              result.error instanceof ApiError
                ? (ploteaApiError(result.error.serverCode) ??
                  'Link wygasł lub został już użyty.')
                : 'Link wygasł lub został już użyty.'
            }
            tone="error"
          />
          <Button
            className="w-full"
            data-testid="go-to-login"
            onClick={() => void nav({ to: '/login', replace: true })}
          >
            Przejdź do logowania
          </Button>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Logowanie…</p>
      )}
    </AuthShell>
  )
}

/**
 * `/verify-email?token=` (link from the verification email): the API
 * verifies the token and redirects back to `/login?verified=true`.
 */
export function PloteaVerifyLinkPage() {
  const token =
    typeof window === 'undefined'
      ? ''
      : (new URLSearchParams(window.location.search).get('token') ?? '')
  if (typeof window !== 'undefined' && token !== '') {
    window.location.replace(
      `${api.url('/auth/verify-email')}?token=${encodeURIComponent(token)}`,
    )
  }
  return (
    <AuthShell>
      {token === '' ? (
        <NoticeBanner
          message="Link weryfikacyjny jest niepełny."
          tone="error"
        />
      ) : (
        <p className="text-sm text-muted-foreground">Weryfikacja adresu…</p>
      )}
    </AuthShell>
  )
}
